// ============================================================
// FILE: @theme/components/common/ecg12-lead-static-waveform/ecg12-lead-static-waveform.component.ts
//
// Renders a SAVED 12-lead ECG capture (the JSON payload
// TriageComponent.finishEcgStandalone11Session() uploads, shape
// { samples: Leads12[], sampleRateHz, ... }) for after-the-fact review --
// e.g. the doctor viewing a triage capture from the EHR panel during a live
// consultation, where there is no physical ECG Standalone 11 device
// connected. Adapts the canvas-drawing/auto-scale logic from
// pages/triage/guided-capture/ecg12-lead-waveform/ecg12-lead-waveform.component.ts
// (the LIVE oscilloscope view), but that component can't be reused directly:
// it injects Ecg11ManagerService and only draws while a device is actually
// streaming (state.connection === 'connected' && isStreaming && warmedUp) --
// there is no @Input() path into it at all. This component instead takes the
// full sample array as a plain @Input() and draws the whole static trace in
// one pass, with the scale computed from the complete array instead of a
// rolling calibration window.
// ============================================================
import { AfterViewInit, Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import { Leads12 } from '../../../../@core/interfaces/device-integration/device-item.model';

const LEAD_NAMES: (keyof Leads12)[] = ['I', 'II', 'III', 'aVR', 'aVL', 'aVF', 'V1', 'V2', 'V3', 'V4', 'V5', 'V6'];

@Component({
  selector: 'app-ecg12-lead-static-waveform',
  templateUrl: './ecg12-lead-static-waveform.component.html',
  styleUrls: ['./ecg12-lead-static-waveform.component.scss'],
})
export class Ecg12LeadStaticWaveformComponent implements AfterViewInit, OnChanges {
  @ViewChild('canvas', { static: true }) private canvasRef: ElementRef<HTMLCanvasElement>;
  @Input() samples: Leads12[] = [];

  private viewReady = false;

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.render();
  }

  ngOnChanges(): void {
    if (this.viewReady) this.render();
  }

  private render(): void {
    const canvas = this.canvasRef?.nativeElement;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    const width = canvas.width;
    const height = canvas.height;

    ctx.fillStyle = '#0b1220';
    ctx.fillRect(0, 0, width, height);

    const samples = this.samples || [];
    if (!samples.length) return;

    // Same auto-calibration as the live view's resetCalibration()/onSample() --
    // widest-spread lead across the WHOLE saved trace (not a rolling window)
    // padded by 20 becomes the shared scale for all 12 rows.
    const scaleMinMax: Record<string, { min: number; max: number }> = {};
    LEAD_NAMES.forEach(name => { scaleMinMax[name] = { min: Infinity, max: -Infinity }; });
    samples.forEach(s => {
      LEAD_NAMES.forEach(name => {
        const v = s[name];
        const mm = scaleMinMax[name];
        if (v < mm.min) mm.min = v;
        if (v > mm.max) mm.max = v;
      });
    });
    let minScaling = -600, maxScaling = 600;
    let widest = 0; let chosen: { min: number; max: number } | null = null;
    LEAD_NAMES.forEach(name => {
      const mm = scaleMinMax[name];
      const spread = mm.max - mm.min;
      if (spread > widest) { widest = spread; chosen = mm; }
    });
    if (chosen && widest > 1) {
      minScaling = chosen.min - 20;
      maxScaling = chosen.max + 20;
    }

    const rowHeight = height / 12;
    const scaleRange = maxScaling - minScaling;
    const scaleY = scaleRange > 0 ? rowHeight / scaleRange : 0;
    const count = samples.length;

    LEAD_NAMES.forEach((name, row) => {
      const rowTop = row * rowHeight;
      const rowMid = rowTop + rowHeight / 2;

      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, rowTop);
      ctx.lineTo(width, rowTop);
      ctx.stroke();

      ctx.fillStyle = '#7ee787';
      ctx.font = '10px monospace';
      ctx.fillText(name, 4, rowTop + 12);

      ctx.strokeStyle = '#34d399';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      for (let i = 0; i < count; i++) {
        const x = (i / (count - 1 || 1)) * width;
        const y = rowMid - samples[i][name] * scaleY;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    });
  }
}
